import { Fragment } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import Link from '@shared/router/link';
import type { LinkNavigation } from '@shared/router/link';

import { cn } from '@playbook/utils';

import css from './styles.module.css';

export type TabVariant = 'default' | 'pill' | 'adaptable';

const DefaultTabVariant: TabVariant = 'default' as const;

type TabEventHandlers = {
	[K in keyof ComponentProps<'input'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'input'>[K];
};

interface TabDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface TabProperties extends TabEventHandlers, TabDataAttributes {
	/** Shared by every tab in a row. `compositions/tab-group` writes it. */
	variant?: TabVariant;
	disabled?: boolean;

	/** Shared by every tab in a row. `compositions/tab-group` writes it. */
	name?: string;
	value: string;
	checked?: boolean;
	defaultChecked?: boolean;

	label: string;

	/**
	 *	What the tab shows. Rendered straight after the tab and shown only while
	 *	it is checked, by CSS alone, so a `TabGroup` of these switches its
	 *	content without any state.
	 */
	panel?: ReactNode;
}

/**
 *	A radio under the hood: the browser keeps which tab is selected, and the
 *	arrow keys move between the tabs of one row. The input is invisible and
 *	fills the tab, so a click anywhere on it selects.
 */
export function Tab({
	variant = DefaultTabVariant,
	disabled = false,

	name,
	value,
	checked,
	defaultChecked,

	label,

	panel,

	...props
}: TabProperties): ReactNode {
	return (
		<Fragment>
			<label className={cn(css.tab, css[variant])}>
				<input
					{...props}
					type="radio"
					name={name}
					value={value}
					checked={checked}
					defaultChecked={defaultChecked}
					disabled={disabled}
					className={css.field}
				/>
				<span className={css.label}>{label}</span>
			</label>
			{panel !== undefined && <div className={css.panel}>{panel}</div>}
		</Fragment>
	);
}

type TabLinkEventHandlers = {
	[K in keyof ComponentProps<'a'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'a'>[K];
};

interface TabLinkProperties extends TabLinkEventHandlers, TabDataAttributes {
	/** Shared by every tab in a row. `compositions/link-tab-group` writes it. */
	variant?: TabVariant;
	/** Shared by every tab in a row. `compositions/link-tab-group` writes it. */
	navigation?: LinkNavigation;

	href: string;
	label: string;
}

/**
 *	A tab that is a link. The router's `Link` marks the tab for the current
 *	page with `data-current-page`, which is what selects it.
 */
export function TabLink({
	variant = DefaultTabVariant,
	navigation,

	href,
	label,

	...props
}: TabLinkProperties): ReactNode {
	return (
		<Link
			{...props}
			href={href}
			navigation={navigation}
			className={cn(css.tab, css[variant], css.link)}>
			<span className={css.label}>{label}</span>
		</Link>
	);
}
